import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  LayoutDashboard,
  Droplets,
  HeartHandshake,
  Flame,
  Users,
  HandHeart,
  CalendarDays,
  Info,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Building2,
  BookOpen,
  UserCog,
  User,
  TreePine,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const menuItems = [
  { path: "/", label: "Panel Principal", icon: LayoutDashboard },
  { path: "/bautizos", label: "Bautizos", icon: Droplets },
  { path: "/matrimonios", label: "Matrimonios", icon: HeartHandshake },
  { path: "/confirmaciones", label: "Confirmaciones", icon: Flame },
  { path: "/personas", label: "Clérigos y Feligreses", icon: Users },
  { path: "/arbol-genealogico", label: "Árbol Genealógico", icon: TreePine },
  { path: "/ministerios", label: "Ministerios", icon: HandHeart },
  { path: "/intenciones", label: "Intenciones de Misa", icon: CalendarDays },
  { path: "/info", label: "Información General", icon: Info },
  { type: "divider" },
  { path: "/entidades", label: "Entidades Jurisdiccionales", icon: Building2 },
  { path: "/usuarios", label: "Gestión de Usuarios", icon: UserCog, adminOnly: true },
  { path: "/documentacion", label: "Documentación / FAQ", icon: BookOpen },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getInitials = (nombre, apellido) => {
    return `${nombre?.charAt(0) || ""}${apellido?.charAt(0) || ""}`.toUpperCase();
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-72 bg-white border-r border-stone-200 transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-stone-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-serif text-rose-950 tracking-tight">
                  ECCLESIA
                </h1>
                <p className="text-xs text-stone-500 mt-1 tracking-widest uppercase">
                  Servicios Parroquiales
                </p>
              </div>
              <button
                className="lg:hidden p-2 hover:bg-stone-100 rounded-md"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-5 h-5 text-stone-600" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item, index) => {
              if (item.type === "divider") {
                return (
                  <div key={`divider-${index}`} className="my-4 border-t border-stone-200" />
                );
              }
              // Hide admin-only items for non-admin users
              if (item.adminOnly && !["super_admin", "admin_conferencia", "admin_diocesano"].includes(user?.rol)) {
                return null;
              }
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md sidebar-item ${
                    isActive
                      ? "active bg-rose-950/5 text-rose-950"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                  data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="text-sm">{item.label}</span>
                  {isActive && (
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-stone-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-3 rounded-md hover:bg-stone-100 transition-colors">
                  <Avatar className="h-9 w-9 bg-rose-950">
                    <AvatarFallback className="bg-rose-950 text-white text-sm">
                      {getInitials(user?.nombre, user?.apellido)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-stone-800">
                      {user?.nombre} {user?.apellido}
                    </p>
                    <p className="text-xs text-stone-500 capitalize">
                      {user?.rol?.replace(/_/g, " ")}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <DropdownMenuItem
                  onClick={() => navigate("/perfil")}
                  className="cursor-pointer"
                  data-testid="perfil-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  Mi Perfil
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden bg-white border-b border-stone-200 p-4 flex items-center gap-4 no-print">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-stone-100 rounded-md"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5 text-stone-600" />
          </button>
          <h1 className="text-xl font-serif text-rose-950">ECCLESIA</h1>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8 animate-fade-in">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
